import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getSortedRowModel,
  ColumnDef,
  SortingState,
} from '@tanstack/react-table';
import styled from 'styled-components';
import { UserInfoDto } from 'services/data-contracts';
import dayjs from 'dayjs';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

interface UserTableProps {
  users: UserInfoDto[];
  handleRowClick?: (row: UserInfoDto) => void;
}

const UserTable = ({ users, handleRowClick }: UserTableProps) => {
  const createColumns = (): ColumnDef<UserInfoDto>[] => [
    {
      accessorKey: 'id',
      header: 'ID',
      enableSorting: true,
      size: 100,
    },
    {
      accessorKey: 'username',
      header: '사용자명',
      enableSorting: true,
      cell: ({ row }) => (
        <UserNameLink
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick?.(row.original);
          }}
        >
          {row.original.username}
        </UserNameLink>
      ),
    },
    {
      accessorKey: 'profile.name',
      header: '이름',
      cell: ({ row }) => row.original.profile?.name || '-',
    },
    {
      accessorKey: 'signupDate',
      header: '가입일',
      cell: ({ getValue }) => dayjs(getValue() as string).format('YYYY-MM-DD'),
      enableSorting: true,
    },
    {
      accessorKey: 'isAdmin',
      header: '관리자',
      cell: ({ getValue }) => (getValue() ? '예' : '아니오'),
      size: 100,
    },
    {
      accessorKey: 'isVerify',
      header: '활성여부',
      cell: ({ getValue }) => (getValue() ? '활성' : '비활성'),
      size: 100,
    },
  ];
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const columns = React.useMemo(() => createColumns(), []);

  const table = useReactTable({
    data: users,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <TableWrapper>
      <StyledTable>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHeader
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                  style={{ width: header.getSize() }}
                >
                  <HeaderContent>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && (
                      <SortIcon>
                        {header.column.getIsSorted() === 'asc' && (
                          <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                        )}
                        {header.column.getIsSorted() === 'desc' && (
                          <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                        )}
                        {!header.column.getIsSorted() && <UnfoldMoreIcon sx={{ fontSize: 16 }} />}
                      </SortIcon>
                    )}
                  </HeaderContent>
                </TableHeader>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              onClick={() => handleRowClick?.(row.original)}
              style={{ cursor: handleRowClick ? 'pointer' : 'default' }}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </tbody>
      </StyledTable>
    </TableWrapper>
  );
};

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border-top: 2px solid #2c2c2c;
`;

const TableHeader = styled.th`
  padding: 12px 16px;
  background-color: white;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  position: sticky;
  top: 0;
  z-index: 1;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const TableRow = styled.tr`
  &:hover {
    background-color: #f9fafb;
  }
`;

const TableCell = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SortIcon = styled.span`
  color: #666;
  display: flex;
  align-items: center;
`;

const UserNameLink = styled.div`
  color: #3b82f6;
  cursor: pointer;

  &:hover {
    color: #1d4ed8;
  }
`;

export default UserTable;
